import { useState } from 'react'
import { Prose } from '../../components/Prose'
import { muscleTr, type CatalogExercise, type ProgramEdits, type WorkoutDay } from '../../lib/program'
import { hacimAciklari, onerilerFor, patternAdi, type Oneri } from '../../lib/hacimOneri'

const n1 = (v: number) => (Math.round(v * 10) / 10).toLocaleString('tr-TR')

/**
 * "Göğsün eksik" ile "göğsü nasıl düzeltirim" arasındaki boşluğu kapatan blok.
 *
 * Tablo zaten eksiği gösteriyordu ama oradan sonrası kullanıcıya kalıyordu:
 * hangi hareket, hangi güne, kaç set kazandırır, seans ne kadar uzar. Kullanıcı
 * bunu kendi kendine yapıyordu ("yetersiz geldi, hareket ekledim") — yani panel
 * hesabı yapıp kararı söylemiyordu.
 *
 * Otomatik EKLEMİYOR. Ekleme düğmesine basan kullanıcı; sayı, süre ve yan etki
 * düğmenin yanında yazıyor. Programın kendi kendine büyümesi seans süresini
 * sessizce şişirir ve kimse neden uzadığını bilmez.
 */
export function HacimOnerileri({ catalog, days, edits, equipment, strengthPerWeek, planned, target, onEkle }: {
  catalog: CatalogExercise[]
  days: WorkoutDay[]
  edits: ProgramEdits
  equipment: string[]
  strengthPerWeek: number
  planned: Record<string, number>
  target: number
  onEkle: (dayLabel: string, slug: string) => void
}) {
  const [acik, setAcik] = useState<string | null>(null)
  const aciklar = hacimAciklari(planned, target)

  if (aciklar.length === 0) {
    return (
      <>
        <h3>Eksik kas var mı</h3>
        <p className="sub" style={{ fontSize: 13.5 }}>
          Programdaki her kas hedefe yakın — eklenecek bir şey görmüyorum.
        </p>
      </>
    )
  }

  return (
    <>
      <h3>Eksik kaslar ve ne yapılabilir</h3>
      <Prose className="card-h">
        Aşağıdakiler programın hedefin (<strong>{target} set/kas</strong>) altında bıraktığı kaslar.
        Her biri için hangi hareketi hangi güne ekleyebileceğini, kaç set kazandıracağını ve
        seansı ne kadar uzatacağını hesapladım. <strong>Kendiliğinden eklenmiyor</strong> —
        kararı sen veriyorsun.
      </Prose>

      <div className="oneriler">
        {aciklar.map((a) => {
          const secili = acik === a.muscle
          const oneriler = secili
            ? onerilerFor({ muscle: a.muscle, catalog, days, edits, equipment, strengthPerWeek, planned })
            : []
          return (
            <div key={a.muscle} className="onericard">
              <button className="onerihead" aria-expanded={secili}
                      onClick={() => setAcik(secili ? null : a.muscle)}>
                <span className="onerikas">{muscleTr(a.muscle)}</span>
                <span className="onerisayi">
                  {n1(a.planned)} / {target} set · <b>{n1(a.eksik)} eksik</b>
                </span>
                <span className="onerioku" aria-hidden>{secili ? '−' : '+'}</span>
              </button>

              {secili && (
                oneriler.length === 0 ? (
                  <p className="note" style={{ margin: '2px 2px 8px' }}>
                    Bu kas için ekipmanınla uygun bir hareket bulamadım — ya katalogda yok,
                    ya günlerin dolu (gün başına en fazla 4 ek hareket), ya da eklemek
                    haftalık tavanı aşıyor.
                  </p>
                ) : (
                  <ul className="onerilist">
                    {oneriler.map((o) => (
                      <OneriSatiri key={o.exercise.slug} o={o} onEkle={onEkle} />
                    ))}
                  </ul>
                )
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}

function OneriSatiri({ o, onEkle }: { o: Oneri; onEkle: (l: string, s: string) => void }) {
  const yan = o.yanKatki.filter((y) => y.sets >= 0.5).slice(0, 3)
  return (
    <li className="onerisatir">
      <div className="onerisatir-main">
        <div className="onerisatir-ad">{o.exercise.nameTr}</div>
        <div className="onerisatir-meta">
          {o.dayName} · {patternAdi(o.pattern)} · {o.sets} set
          {!o.birincil && ' · ikincil kas'}
        </div>
        <div className="onerisatir-etki">
          <span className="pill pill-good">{muscleTr(o.muscle)} +{n1(o.kazanc)} → {n1(o.sonrasi)}</span>
          <span className="pill">seans +{o.ekDakika} dk</span>
          {yan.map((y) => (
            <span key={y.muscle} className="pill">{muscleTr(y.muscle)} +{n1(y.sets)}</span>
          ))}
        </div>
      </div>
      <button className="primary" onClick={() => onEkle(o.dayLabel, o.exercise.slug)}>
        Ekle
      </button>
    </li>
  )
}
